import React, { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
    TextField,
    InputAdornment,
    Tooltip,
    Button,
    Box,
    IconButton,
    Typography,
    // MenuItem
} from '@mui/material';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { ArrowBack, FileDownloadOutlined } from '@mui/icons-material';
import { first, isEmpty } from 'lodash';
import { useSelector } from 'react-redux';
import { getRepositoryBatchFiles } from '../../apis/repository';
import StatusBadge from '../StatusBadge';
import { useDebounce } from '../../hooks/useDebounce';
import { formatDate, formatTime, handleFileDownload } from '../../lib/utils';
import FileStatusListener from '../dashboard/FileStatusListener';
import TablePagination from '../TablePagination';

const BatchPackageList = ({ batchId, batchDetails, pageSize = 10 }) => {
    const navigate = useNavigate();
    const user = useSelector((state) => state.auth.user);
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [totalCount, setTotalCount] = useState(0);
    const [sortModel, setSortModel] = React.useState(null);
    const [paginationModel, setPaginationModel] = useState({
        pageSize,
        page: 0,
    });

    const debouncedSearch = useDebounce(search, 500);

    useEffect(() => {
        loadFiles();
    }, [batchId, paginationModel.page, paginationModel.pageSize, debouncedSearch, statusFilter, sortModel]);

    const loadFiles = async () => {
        if (!batchId) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const response = await getRepositoryBatchFiles(
                batchId,
                paginationModel.page + 1,
                paginationModel.pageSize,
                search,
                statusFilter,
                sortModel
            );
            const fileList = response.results || [];
            setFiles(fileList);
            setTotalCount(response.pagination.total_count);
        } catch (error) {
            // console.error('Error loading repository batch files:', error);
            toast.error('Failed to load repository batch files');
            setFiles([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    };

    const handleSortModelChange = async (sortOption) => {
        if (isEmpty(sortOption)) {
            setSortModel(null);
            return;
        }

        const { field, sort } = first(sortOption);
        setSortModel({ field, sort });
    };

    const handleJobStatusUpdate = (jobStatus) => {
        if (!jobStatus || jobStatus.jobId == null) {
            return;
        }

        const jobId = Number(jobStatus.jobId);

        // jobId corresponds to scorm_files.id (the file ID)
        setFiles((prevFiles) => {
            const fileIndex = prevFiles.findIndex((file) => Number(file?.id) === jobId);

            if (fileIndex !== -1) {
                const updatedFiles = [...prevFiles];
                const file = updatedFiles[fileIndex];
                const nextLastExtraction = file.last_extraction
                    ? {
                          ...file.last_extraction,
                          status: jobStatus?.status ?? file.last_extraction.status,
                          ...(jobStatus.download_url
                              ? { download_link: jobStatus.download_url }
                              : {}),
                      }
                    : jobStatus.download_url
                      ? {
                            status: jobStatus?.status,
                            download_link: jobStatus.download_url,
                        }
                      : file.last_extraction;

                updatedFiles[fileIndex] = {
                    ...file,
                    status: jobStatus.status,
                    last_extraction: nextLastExtraction,
                };

                return updatedFiles;
            }

            // If file doesn't exist in current view, refresh the list
            loadFiles();
            return prevFiles;
        });
    };

    // Get orgId from user - must be organization_id
    const orgId = user?.organization_id;

    const columns = [
        {
            field: 'file_name',
            headerName: 'File Name',
            flex: 3,
            renderCell: (params) => <Tooltip title={params?.value}>{params?.value}</Tooltip> || '-',
        },
        {
            field: 'created_at',
            headerName: 'Date',
            flex: 1,
            valueFormatter: formatDate,
        },
        {
            field: 'last_extraction_started_at',
            headerName: 'Start Time',
            flex: 1,
            renderCell: (params) => formatTime(params?.row?.last_extraction?.started_at) || '-',
        },
        {
            field: 'last_extraction_finished_at',
            headerName: 'Finish Time',
            flex: 1,
            renderCell: (params) => formatTime(params?.row?.last_extraction?.finished_at) || '-',
        },
        {
            field: 'last_extraction_status',
            headerName: 'Status',
            flex: 1,
            renderCell: (params) => {
                const remark = params?.row?.last_extraction?.remark || params?.row?.remark || "Extraction failed due to timeout";
                const statusBadge = (
                    <StatusBadge
                        status={
                            params?.row?.last_extraction == null
                                ? params?.row?.status || '-'
                                : params?.row?.last_extraction?.status || '-'
                        }
                        type={params?.row?.last_extraction == null ? 'file' : 'extraction'}
                    />
                );

                return remark ? (
                    <Tooltip title={remark} arrow placement="top">
                        <span>{statusBadge}</span>
                    </Tooltip>
                ) : (
                    statusBadge
                );
            },
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 0.5,
            sortable: false,
            renderCell: (params) => {
                const download_link = params?.row?.last_extraction?.download_link ?? null;

                return (
                    <Tooltip title="Download File">
                        <IconButton
                            size="small"
                            disabled={download_link === null}
                            onClick={() => handleFileDownload(download_link)}
                        >
                            <FileDownloadOutlined color={download_link === null ? '#4C43A366' : 'primary'} />
                        </IconButton>
                    </Tooltip>
                );
            },
        },
    ];

    return (
        <Box>
            {orgId && (
                <FileStatusListener orgId={orgId} onJobStatusUpdate={handleJobStatusUpdate} />
            )}
            <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={() => navigate(-1)}
                sx={{ minWidth: 'auto', my: 1, border: 0 }}
            >
                Back
            </Button>

            <Box variant="div" className="custom_table_container">
                <Box variant="div" className="table_filters">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Typography variant="h6" fontWeight={600}>
                            {batchId && batchDetails
                                ? `${batchDetails?.batch_name ?? ''} (${batchDetails?.repository_name ?? ''})`
                                : 'All Files'}
                        </Typography>
                    </Box>
                    <Box className="filter_box">
                        <TextField
                            size="small"
                            placeholder="Search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <span className="material-symbols-rounded">search</span>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                        {/* <TextField
                            select
                            label="Filter by status"
                            size="small"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            style={{ minWidth: 180 }}
                        >
                            <MenuItem value="all">All Status</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="approved">Approved</MenuItem>
                            <MenuItem value="rejected">Rejected</MenuItem>
                            <MenuItem value="processing">Processing</MenuItem>
                        </TextField> */}
                    </Box>
                </Box>

                <Box variant="div" className="custom_table_responsive">
                    <DataGrid
                        rows={files}
                        columns={columns}
                        getRowId={(row) => row.id}
                        autoHeight
                        pageSizeOptions={[5, 10, 25, 50]}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        pagination
                        paginationMode="server"
                        loading={loading}
                        disableRowSelectionOnClick
                        disableColumnMenu
                        localeText={{
                            noRowsLabel: 'No Files Found',
                            sortLabel: '',
                        }}
                        rowCount={totalCount}
                        sortingMode="server"
                        onSortModelChange={handleSortModelChange}
                        slots={{
                            pagination: () => (
                                <TablePagination
                                    paginationModel={paginationModel}
                                    onPaginationModelChange={setPaginationModel}
                                    totalCount={totalCount}
                                    pageSizeOptions={[5, 10, 25, 50]}
                                />
                            ),
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default BatchPackageList;
